import { setMilliseconds, sub } from 'date-fns';
import { createWithEqualityFn as create } from 'zustand/traditional';

import {
  filtersForGroupType,
  receivingClientFilterKey,
  receivingGroupFilterKey,
} from 'components/insights/careTransitions/filters';
import {
  DATES,
  END_DATE,
  PREDECESSOR_GROUP_TYPE,
  PREDECESSOR_LOCATION,
  START_DATE,
} from 'constants/filterKeysConstants';
import GroupType from 'models/GroupType';

import { FilterOption, FilterState } from './types';

const now = setMilliseconds(new Date(), 0);
const thirtyDaysAgo = sub(now, { days: 30 });

export type NetworkPerformanceFilterState = FilterState<FilterOption[] | string | Date>;

import type { NetworkPerformanceMetricDrillKey } from 'components/insights/careTransitions/networkPerformance/slideout/constants';

export type { NetworkPerformanceMetricDrillKey };

export type NetworkPerformanceSlideoutContext =
  | { kind: 'metric'; metric: NetworkPerformanceMetricDrillKey }
  | { kind: 'entity'; entityId: string; entityName: string };

type Actions = {
  setSelectedGroupType: (groupType: GroupType | undefined) => void;
  setSelectedFilters: (filters: NetworkPerformanceFilterState) => void;
  resetFilters: () => void;
  setExpanded: (expanded: boolean) => void;
  openSlideout: (metric: NetworkPerformanceMetricDrillKey) => void;
  openEntitySlideout: (entityId: string, entityName: string) => void;
  closeSlideout: () => void;
};

type State = {
  selectedGroupType: GroupType | undefined;
  filters: NetworkPerformanceFilterState;
  expanded: boolean;
  slideoutContext: NetworkPerformanceSlideoutContext | null;
  actions: Actions;
};

export const useNetworkPerformanceStore = create<State>((set) => ({
  selectedGroupType: undefined,
  expanded: false,
  slideoutContext: null,
  filters: {
    [START_DATE]: thirtyDaysAgo,
    [END_DATE]: now,
  },
  actions: {
    setSelectedGroupType: (groupType) => set({ selectedGroupType: groupType }),
    setSelectedFilters: (filters) => set((state) => ({ filters: { ...state.filters, ...filters } })),
    resetFilters: () =>
      set({
        filters: {
          [START_DATE]: thirtyDaysAgo,
          [END_DATE]: now,
        },
      }),
    setExpanded: (expanded) => set({ expanded }),
    openSlideout: (metric) => set({ slideoutContext: { kind: 'metric', metric } }),
    openEntitySlideout: (entityId, entityName) => set({ slideoutContext: { kind: 'entity', entityId, entityName } }),
    closeSlideout: () => set({ slideoutContext: null }),
  },
}));

export const useNetworkPerformanceActions = () => useNetworkPerformanceStore((state) => state.actions);

export const getNetworkPerformanceAnalyticsFilters = ({ filters, selectedGroupType }: State) => {
  if (!selectedGroupType) return null;

  const receivingClientKey = receivingClientFilterKey(selectedGroupType);
  const receivingGroupKey = receivingGroupFilterKey(selectedGroupType);
  const activeFilters = filtersForGroupType(filters, selectedGroupType, PREDECESSOR_LOCATION, PREDECESSOR_GROUP_TYPE);

  const {
    [START_DATE]: start,
    [END_DATE]: end,
    [receivingClientKey]: providerClient,
    [receivingGroupKey]: group,
    ...rest
  } = activeFilters;

  return {
    ...rest,
    [DATES]: [{ start, end }],
    providerClient,
    group,
    groupType: selectedGroupType.id,
  };
};
